import { CustomAxiosErrorType } from '@ukpostbox/client-data';
import { Button, ColourStateSolid, FlexGap, StackHorizontal, useTranslation } from '@ukpostbox/frontend-framework';
import Lottie from 'lottie-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import notFound from '../Animations/not-found.json';
import { useSite } from '../Providers/SiteInterfaceProvider';

type ErrorPageType = {
	error?: Error;
};
type FormatErrorCauseType = {
	cause?: unknown;
};
type FormatErrorStackType = {
	stack?: string;
};

function FormatErrorCause(props: FormatErrorCauseType) {
	if (!props.cause) return null;

	return <div style={{ whiteSpace: 'pre' }}>{props.cause.toString()}</div>;
}

function FormatErrorStack(props: FormatErrorStackType) {
	if (!props.stack) return null;

	const newStack = props.stack.split('\n');

	if (newStack.length > 21) {
		newStack.splice(21, newStack.length - 21);
	}

	return (
		<>
			{newStack.map((line, index) => {
				const method = line?.split('@')[0];
				const location = line?.split('@')[1]?.replace(/:/, '');
				const numbers = line?.split(':');

				return (
					<div
						key={`errorStackEntry-${method}`}
						className='error-stack-entry'
						style={{ opacity: (100 - index * 5) / 100 }}
					>
						<strong>{method}</strong>
						<strong>{location}</strong>
						<span>
							{numbers[numbers.length - 2]}:{numbers[numbers.length - 1]}
						</span>
					</div>
				);
			})}
		</>
	);
}

function ErrorPage(props: ErrorPageType) {
	const navigate = useNavigate();

	const { t } = useTranslation();
	const { currentLocale, updatePageMeta, UpdateSiteContact, sentryErrorId, HandleError } = useSite();

	const [historyData, setHistoryData] = useState<CustomAxiosErrorType | null>(null);

	const LogError = () => {
		if (props.error) {
			HandleError(props.error);
		}
	};

	useEffect(() => {
		if (window.history.state) {
			setHistoryData(window.history.state);
		}
	}, []);

	useEffect(() => {
		updatePageMeta({
			title: t('errorPage.title'),
		});
	}, [currentLocale]);

	useEffect(() => {
		if (props.error) {
			LogError();
		}
	}, [props.error]);

	return (
		<div className='flex-column align-center justify-center p-lg'>
			<div className='animation-framer'>
				<Lottie animationData={notFound} loop />
			</div>

			<h1 className='mt-md mb-xs'>
				{historyData?.message || historyData?.errorRequestMessage || props.error?.toString()}
			</h1>

			<p className='text-center'>{t('errorPage.messages.description')}</p>
			<p className='text-center mb-xxl'>{t('errorPage.messages.apology')}</p>

			<StackHorizontal gap={FlexGap.Small} extraClass='mb-xxl'>
				<Button
					title={t('errorPage.actions.accountOverview')}
					onClick={() => {
						navigate('/');
					}}
					colour={ColourStateSolid.Secondary}
				/>
				<Button
					title={t('errorPage.actions.messageUs')}
					onClick={() => {
						UpdateSiteContact(true, 4, `Error Fingerprint: ${sentryErrorId}`);
					}}
					colour={ColourStateSolid.Secondary}
				/>
			</StackHorizontal>

			<div className='container-narrow p-xxl mb-xl error-trace-container'>
				<div className='container-narrow p-xxl mb-xl error-trace-container'>
					<h4>{t('errorPage.labels.errorTrace')}</h4>
					<p>{sentryErrorId}</p>

					<FormatErrorCause cause={historyData ? historyData.cause : props.error?.cause} />
					<FormatErrorStack stack={historyData ? historyData.stack : props.error?.stack} />
				</div>
			</div>
		</div>
	);
}

export default ErrorPage;
